// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import merge from 'lodash-es/merge';
import { BehaviorSubject, Observable, map, of } from 'rxjs';
import { LocalDateFormat } from '../../../_interfaces/data/local-data-format-model';
import { Settings } from '../../../_interfaces/settings-model';
import localDateFormatsJson from '../../../config/localDateFormats.json';

const cookieName = 'settingsCookie';

const defaultSettings: Settings = {
  isTimelineInAscendingOrder: false,
  localDateFormatId: 1,
  modalityId: 1,
  modalityIds: [1, 12, 10, 19, 5, 2],
  lastConfirmedAnnouncementIndex: null
};

@Injectable()
export class SettingsService {
  settings$: BehaviorSubject<Settings> = new BehaviorSubject(
    SettingsService.initialSettings()
  );

  dateFormat$: BehaviorSubject<LocalDateFormat> =
    new BehaviorSubject<LocalDateFormat>(
      SettingsService.deriveDateFormatFromSettings(this.settings$.getValue())
    );

  // true if timeline is ascending
  timelineAscending$: Observable<boolean> = this.settings$.pipe(
    map((settings) => settings.isTimelineInAscendingOrder)
  );

  // array of currently active modality ids
  modalityIds$: Observable<number[]> = this.settings$.pipe(
    map((settings) => settings.modalityIds)
  );

  userRegion$ = of(navigator.language.split('-')[1]);

  private static deriveDateFormatFromSettings(settings: Settings) {
    const currentDateFormat = localDateFormatsJson.filter(
      (localDateFormat) => localDateFormat.id === settings.localDateFormatId
    )[0];
    return currentDateFormat || localDateFormatsJson[0];
  }
  // ONLY USE THIS IF YOU NEED SETTINGS ONLY ONCE!
  // use the observables if you need to react to data
  public getSettings(): Settings {
    return this.settings$.getValue();
  }

  public updateSettings(settings: Partial<Settings>): void {
    const updatedSettings = merge(
      structuredClone(this.settings$.getValue()),
      settings
    );
    // just to be safe, make sure modalities are unique
    if (settings.modalityIds) {
      updatedSettings.modalityIds = settings.modalityIds.filter((v, i, a) => {
        return a.indexOf(v) === i;
      });
    }
    localStorage.removeItem(cookieName); //deleting old cookies as it was polluted , should be removed in next version
    localStorage.setItem(cookieName, JSON.stringify(updatedSettings));

    this.settings$.next(updatedSettings);
    this.dateFormat$.next(
      SettingsService.deriveDateFormatFromSettings(updatedSettings)
    );
  }

  private static initialSettings(): Settings {
    const stored = localStorage.getItem(cookieName);
    if (stored) {
      // TODO: no validation currently
      const merged = {
        // make sure to also add default settings here in case some settings
        // are missing
        ...defaultSettings,
        ...JSON.parse(stored),
      };
      return merged;
    } else {
      return defaultSettings;
    }
  }

  // there are several places where modalities are toggled
  // via checlkboxes so we moved this handler up to this service
  onToggleModalityActivation(
    id: number,
    event: { target?: HTMLElement }
  ): void {
    const settings = structuredClone(this.settings$.getValue());

    const isActive = event.target?.getAttribute('active') !== null;

    const currentlyActive =
      settings.modalityIds.filter((i: number) => i === id).length > 0;
    if (currentlyActive === isActive) {
      return;
    }

    settings.modalityIds = [
      ...settings.modalityIds.filter((m: number) => m !== id),
      ...(isActive ? [id] : []),
    ];

    this.updateSettings(settings);
  }
}
